import { useState, useEffect, useMemo, useRef } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';

const STATUS_TABS = [
  { key: 'pending_call', label: 'Pending Calls' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'invalid_number', label: 'Invalid Number' },
];

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Free-text input with a suggestion dropdown — used for Company and City filters.
function FilterSearchInput({ value, onChange, suggestions, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const base = value
      ? suggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
      : suggestions;
    return base.slice(0, 8);
  }, [suggestions, value]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          {filtered.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => { onChange(s); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentAggregatedApplications() {
  const [status, setStatus] = useState('pending_call');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [saving, setSaving] = useState({});

  const [searchInput, setSearchInput] = useState('');
  const [companyInput, setCompanyInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput);
  const debouncedCompany = useDebouncedValue(companyInput);
  const debouncedCity = useDebouncedValue(cityInput);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const res = await api.get('/agent/aggregated-applications', { params: { status } });
      setApplications(res.data);
    } catch (err) {
      console.error('Failed to load aggregated applications', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [status]);

  const knownCompanies = useMemo(
    () => Array.from(new Set(applications.map((a) => a.job?.company_name).filter(Boolean))).sort(),
    [applications]
  );
  const knownCities = useMemo(
    () => Array.from(new Set(applications.map((a) => a.job?.location).filter(Boolean))).sort(),
    [applications]
  );

  const visibleApplications = useMemo(() => {
    let list = applications;
    if (debouncedCompany) {
      const q = debouncedCompany.toLowerCase();
      list = list.filter((a) => (a.job?.company_name || '').toLowerCase().includes(q));
    }
    if (debouncedCity) {
      const q = debouncedCity.toLowerCase();
      list = list.filter((a) => (a.job?.location || '').toLowerCase().includes(q));
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter((a) =>
        (a.job?.title || '').toLowerCase().includes(q) ||
        (a.job?.company_name || '').toLowerCase().includes(q) ||
        (a.candidate?.name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [applications, debouncedCompany, debouncedCity, debouncedSearch]);

  const hasActiveFilters = searchInput || companyInput || cityInput;
  const clearFilters = () => {
    setSearchInput('');
    setCompanyInput('');
    setCityInput('');
  };

  const handleUpdate = async (id, newStatus) => {
    setSaving((prev) => ({ ...prev, [id]: true }));
    try {
      await api.put(`/agent/aggregated-applications/${id}/status`, {
        status: newStatus,
        agent_notes: notes[id] || undefined,
      });
      setApplications((prev) => prev.filter((a) => a.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      console.error('Failed to update application', err);
    } finally {
      setSaving((prev) => ({ ...prev, [id]: false }));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = visibleApplications.length > 0 && visibleApplications.every((a) => selectedIds.has(a.id));
  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(visibleApplications.map((a) => a.id)));
  };

  const handleBulkUpdate = async (newStatus) => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(
        ids.map((id) =>
          api.put(`/agent/aggregated-applications/${id}/status`, {
            status: newStatus,
            agent_notes: notes[id] || undefined,
          })
        )
      );
      setApplications((prev) => prev.filter((a) => !ids.includes(a.id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk update failed', err);
      alert('Some applications failed to update. Please refresh and try again.');
      fetchApplications();
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <AgentLayout>
      <div className="bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9] -m-6 p-6 min-h-screen">
       <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent mb-1">Aggregated Job Applications</h2>
<p className="text-gray-500 text-sm mb-6">
          Call the company to confirm the opening before marking it confirmed.
        </p>

        <div className="flex gap-2 mb-4 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatus(tab.key)}
             className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${
  status === tab.key
    ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
    : 'bg-white text-gray-600 border-gray-200 hover:bg-purple-50'
}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text"
              placeholder="Search title, company, candidate…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full border border-gray-200 rounded-full px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
            <FilterSearchInput
              value={companyInput}
              onChange={setCompanyInput}
              suggestions={knownCompanies}
              placeholder="Company"
            />
            <FilterSearchInput
              value={cityInput}
              onChange={setCityInput}
              suggestions={knownCities}
              placeholder="City"
            />
          </div>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-xs font-semibold text-purple-600 hover:underline mt-3">
              Clear filters
            </button>
          )}
        </div>

        {status === 'pending_call' && selectedIds.size > 0 && (
         <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-4 py-2.5 mb-4 flex-wrap">
  <span className="text-xs font-semibold text-gray-600">{selectedIds.size} selected</span>
            <button
              disabled={bulkSaving}
              onClick={() => handleBulkUpdate('confirmed')}
              className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm hover:opacity-90 disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              disabled={bulkSaving}
              onClick={() => handleBulkUpdate('rejected')}
              className="bg-gradient-to-r from-red-500 to-rose-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm hover:opacity-90 disabled:opacity-50"
            >
              Reject
            </button>
            <button
              disabled={bulkSaving}
              onClick={() => handleBulkUpdate('invalid_number')}
              className="bg-gray-400 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-sm hover:bg-gray-500 disabled:opacity-50"
            >
              Invalid Number
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : visibleApplications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-400">
            No applications match these filters.
          </div>
        ) : (
          <div className="space-y-3">
            {status === 'pending_call' && (
              <label className="flex items-center gap-2 text-sm text-gray-500 px-1">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                Select all
              </label>
            )}
            {visibleApplications.map((app) => (
              <div key={app.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:bg-purple-50/30 transition-colors">
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    {status === 'pending_call' && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(app.id)}
                        onChange={() => toggleSelect(app.id)}
                        className="mt-1 accent-purple-600"
                      />
                    )}
                    <div>
                      <h3 className="font-bold text-gray-900">{app.job.title}</h3>
                      <p className="text-sm text-gray-600">{app.job.company_name} · {app.job.location}</p>
                      <p className="text-sm text-purple-600 font-semibold mt-1">
                        Company mobile: {app.job.company_mobile || 'Not available'}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-700 text-right">
                    <p className="font-semibold">{app.candidate.name}</p>
                    <p>{app.candidate.phone}</p>
                    <p>{app.candidate.email}</p>
                    {app.candidate.resume_path && (
                      <a
                        href={`${BACKEND_URL}/storage/${app.candidate.resume_path}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block mt-1 text-purple-600 hover:underline text-xs font-semibold"
                      >
                        View Resume
                      </a>
                    )}
                    {app.candidate.city && (
                      <p className="text-xs text-gray-600 mt-2">
                        <span className="font-semibold">City:</span> {app.candidate.city}{app.candidate.state ? `, ${app.candidate.state}` : ''}
                      </p>
                    )}
                    {app.candidate.skills && (
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold">Skills:</span> {Array.isArray(app.candidate.skills) ? app.candidate.skills.map(s => s.name || s).join(', ') : app.candidate.skills}
                      </p>
                    )}
                    {app.candidate.career_objective && (
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold">About:</span> {app.candidate.career_objective}
                      </p>
                    )}
                  </div>
                </div>

                {status === 'pending_call' && (
                  <div className="mt-3">
                    <textarea
                      placeholder="Call notes (optional)"
                      value={notes[app.id] || ''}
                      onChange={(e) => setNotes((prev) => ({ ...prev, [app.id]: e.target.value }))}
                     className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={saving[app.id]}
                        onClick={() => handleUpdate(app.id, 'confirmed')}
                        className="bg-green-600 text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-green-700"
                      >
                        Confirm
                      </button>
                      <button
                        disabled={saving[app.id]}
                        onClick={() => handleUpdate(app.id, 'rejected')}
                        className="bg-red-500 text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-red-600"
                      >
                        Reject
                      </button>
                      <button
                        disabled={saving[app.id]}
                        onClick={() => handleUpdate(app.id, 'invalid_number')}
                        className="bg-gray-400 text-white text-sm font-semibold px-4 py-1.5 rounded-lg hover:bg-gray-500"
                      >
                        Invalid Number
                      </button>
                    </div>
                  </div>
                )}

                {app.agent_notes && (
                  <p className="text-xs text-gray-500 mt-2">Notes: {app.agent_notes}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AgentLayout>
  );
}

export default AgentAggregatedApplications;